import React, { useState, useMemo, useCallback } from 'react';
import { Box, Typography, Avatar, Switch, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  useGetBrandsQuery,
  useDeleteBrandMutation,
  useUpdateBrandMutation,
} from 'state/api';
import {
  PageContainer,
  DataTable,
  TableToolbar,
  StatusChip,
  normalizeList,
} from 'components/shared';
import EditBrandDialog from './EditBrandDialog';

const BrandList = () => {
  const [search, setSearch] = useState('');
  const [editBrand, setEditBrand] = useState(null);
  const { data, isFetching } = useGetBrandsQuery({ search });
  const [deleteBrand] = useDeleteBrandMutation();
  const [updateBrand] = useUpdateBrandMutation();

  const { rows } = useMemo(() => normalizeList(data), [data]);

  const handleEdit = useCallback((brand) => setEditBrand(brand), []);

  const handleToggle = async (brand) => {
    const newStatus = brand.status === 'active' ? 'inactive' : 'active';
    await updateBrand({ id: brand._id, status: newStatus }).unwrap().catch(() => {});
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this brand?')) {
      await deleteBrand(id).unwrap().catch(() => window.alert('Failed to delete'));
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Brand',
        render: (row) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              variant="rounded"
              alt={row.name}
              sx={{ width: 40, height: 40, bgcolor: 'action.selected' }}
            >
              {(row.name || '?').charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="body2" fontWeight={600}>
              {row.name}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        render: (row) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch
              size="small"
              checked={row.status === 'active'}
              onChange={(e) => {
                e.stopPropagation();
                handleToggle(row);
              }}
            />
            <StatusChip status={row.status || 'inactive'} />
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        align: 'right',
        render: (row) => (
          <>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(row);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(row._id);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleEdit]
  );

  return (
    <PageContainer
      title="Brands"
      subtitle="Manage the brands available across your catalogue"
    >
      <TableToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by brand name…"
      />
      <DataTable
        columns={columns}
        rows={rows}
        loading={isFetching}
        emptyText="No brands yet"
      />
      <EditBrandDialog
        open={Boolean(editBrand)}
        brand={editBrand}
        onClose={() => setEditBrand(null)}
        onError={(msg) => window.alert(msg)}
      />
    </PageContainer>
  );
};

export default BrandList;
